import { EDITION_CAPABILITIES, type EditionCapabilities } from '@dbrain/shared';
import { Spinner } from '@dbrain/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import { useMe } from '../api/hooks.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { useMeta } from '../experience.js';

function Loading() {
  const { t } = useTranslation();
  return (
    <div className="page-center">
      <Spinner label={t('common.loading')} />
    </div>
  );
}

/** Signed-in users only; others go to the login page and come back afterwards. */
export function RequireAuth({ children, guest }: { children: ReactNode; guest?: ReactNode }) {
  const me = useMe();
  // the shell names the experience of this address; drawn before it is known, the code address would flash CompanyGPT
  const meta = useMeta();
  const location = useLocation();
  if (me.isPending || meta.isPending) return <Loading />;
  if (me.isError) {
    return (
      <div className="page-center">
        <ErrorAlert error={me.error} />
      </div>
    );
  }
  // Phase 16c: a page with its own signed-out view (the bireysel landing page) draws it instead of the sign-in
  if (!me.data) return guest !== undefined ? <>{guest}</> : <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

/**
 * Phase 13d: a page the edition offers (sign-up, invitations and password
 * reset are not in the kurumsal edition); otherwise the sign-in page.
 */
export function RequireCapability({ name, children }: { name: keyof EditionCapabilities; children: ReactNode }) {
  const meta = useMeta();
  if (meta.isPending) return <Loading />;
  // an API without capabilities is the bireysel edition
  const offered = (meta.data?.capabilities ?? EDITION_CAPABILITIES.bireysel)[name];
  if (!offered) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Pages for signed-out users (login, signup); signed-in users go home. */
export function GuestOnly({ children }: { children: ReactNode }) {
  const me = useMe();
  if (me.isPending) return <Loading />;
  if (me.data) return <Navigate to="/" replace />;
  return <>{children}</>;
}
